import * as React from "react";

/**
 * Names for the id segments of the breadcrumbs (QA-07).
 *
 * The breadcrumbs are built from the URL, so an id reads as its prefix
 * («a6829cae...»). A page that loads the entity behind an id names it here
 * («QA · Frontier E2E») from the query it already makes, and the breadcrumbs
 * show that name while the page is mounted. The layout imports nothing from
 * the feature that names it, so a lazily loaded page stays in its own chunk.
 * The provider is `BreadcrumbLabelsProvider`, mounted by the shell layout.
 */

export type SetBreadcrumbLabel = (
  segment: string,
  label: string | null,
) => void;

export const NO_BREADCRUMB_LABELS: ReadonlyMap<string, string> = new Map();

export const BreadcrumbLabelsContext =
  React.createContext<ReadonlyMap<string, string>>(NO_BREADCRUMB_LABELS);

// Apart from the labels, so a page that names an id does not re-render when
// the names change.
export const SetBreadcrumbLabelContext =
  React.createContext<SetBreadcrumbLabel | null>(null);

/**
 * Names `segment` (an id of the current path) in the breadcrumbs while the
 * caller is mounted. Until `label` is known (the query is loading) the
 * breadcrumbs keep the short id. Without a provider it does nothing.
 */
export function useBreadcrumbLabel(
  segment: string | undefined,
  label: string | null | undefined,
): void {
  const setLabel = React.useContext(SetBreadcrumbLabelContext);
  React.useEffect(() => {
    if (!setLabel || !segment || !label) return;
    setLabel(segment, label);
    return () => setLabel(segment, null);
  }, [setLabel, segment, label]);
}

/** The names pages gave to id segments (none without a provider). */
export function useBreadcrumbLabels(): ReadonlyMap<string, string> {
  return React.useContext(BreadcrumbLabelsContext);
}
